import { DateTime } from "luxon";
import type { Generator } from "../../../../../utils/backend-types";
import type { AusfallarbeitTimeSeriesResponse } from "../../../../RevenueMonitoring/RevenueMonitoring.types";
import { getAbreglerLabel, SLOT_DURATION_MS } from "../Ausfallarbeit.constants";
import type { AusfallarbeitMassnahmeRow } from "../AusfallarbeitTable";

type ActiveSlot = {
  tsMs: number;
  kwh: number;
};

type AggregatedEvent = {
  startMs: number;
  lastMs: number;
  totalKwh: number;
  slots: number;
};

/**
 * Keeps only active entries from the time series.
 * `0` and `null` values do not represent Ausfallarbeit and are ignored.
 */
function getActiveSlots(
  index: string[],
  values: Array<number | null>
): ActiveSlot[] {
  return index.flatMap((timestamp, i) => {
    const kwh = values[i];
    if (!kwh) return [];

    return [
      {
        tsMs: DateTime.fromISO(timestamp).toMillis(),
        kwh
      }
    ];
  });
}

/**
 * Merges consecutive active slots into one event.
 * Two slots belong to the same event when the gap between their timestamps does
 * not exceed the expected slot duration.
 */
function groupActiveSlotsIntoEvents(
  activeSlots: ActiveSlot[]
): AggregatedEvent[] {
  const events: AggregatedEvent[] = [];

  for (const slot of activeSlots) {
    const currentEvent = events.at(-1);
    const isNewEvent =
      !currentEvent || slot.tsMs - currentEvent.lastMs > SLOT_DURATION_MS;

    if (isNewEvent) {
      events.push({
        startMs: slot.tsMs,
        lastMs: slot.tsMs,
        totalKwh: slot.kwh,
        slots: 1
      });
    } else {
      currentEvent.lastMs = slot.tsMs;
      currentEvent.totalKwh += slot.kwh;
      currentEvent.slots += 1;
    }
  }

  return events;
}

/**
 * Builds table rows by converting active time-series slots into contiguous
 * Ausfallarbeit events per generator and abregler.
 */
export function buildAusfallarbeitTableRows(
  ausfallarbeitData: AusfallarbeitTimeSeriesResponse | undefined,
  generators: Array<Generator>
): AusfallarbeitMassnahmeRow[] {
  const generatorMap = new Map(generators.map((g) => [g.id, g]));

  return (ausfallarbeitData ?? []).flatMap((series) => {
    const generator = generatorMap.get(series.generator_id);
    const generatorName = generator
      ? `${generator.name} (${generator.trId ?? "keine TR-ID"})`
      : "Unbekannter Erzeuger";
    const typ = getAbreglerLabel(series.abregler);
    const activeSlots = getActiveSlots(series.index, series.values);
    const events = groupActiveSlotsIntoEvents(activeSlots);

    return events.map((event, eventIndex) => ({
      id: `${series.generator_id}-${series.abregler ?? ""}-${eventIndex}`,
      startMs: event.startMs,
      endMs: event.lastMs + SLOT_DURATION_MS,
      durationMs: event.slots * SLOT_DURATION_MS,
      generatorName,
      typ,
      mengeKwh: event.totalKwh
    }));
  });
}
